import { relations } from 'drizzle-orm'
import { boolean, index, integer, pgEnum, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core'
import { createInsertSchema } from 'drizzle-zod'
import { z } from 'zod'
import { receivables } from '../receivables/receivables.schema'
import { createdAt, id, updatedAt } from '../utils/columns'
import { entities } from './entities.schema'

export const paymentReminderTypeEnum = pgEnum('payment_reminder_type', [
  'payment_reminder',
  'overdue_reminder',
])
export const paymentReminderTypeSchema = z.enum(paymentReminderTypeEnum.enumValues)
export type PaymentReminderType = z.infer<typeof paymentReminderTypeSchema>

export const reminderEmailTemplates = pgTable(
  'reminder_email_templates',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    name: text('name').notNull(),
    subject: text('subject').notNull(),
    body: text('body').notNull(),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_reminder_email_templates_entity_id').on(table.entityId),
      index('idx_reminder_email_templates_created_at').on(table.createdAt),
    ]
  },
)

export const paymentReminders = pgTable(
  'payment_reminders',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    name: text('name').notNull(),
    intervalDays: integer('interval_days').notNull(),
    isActive: boolean('is_active').notNull().default(true),
    type: paymentReminderTypeEnum('type').notNull(),
    emailTemplateId: text('email_template_id')
      .notNull()
      .references(() => reminderEmailTemplates.id),
    createdAt,
    updatedAt,
  },
  (table) => {
    return [
      index('idx_payment_reminders_entity_id').on(table.entityId),
      index('idx_payment_reminders_type').on(table.type),
      index('idx_payment_reminders_entity_type').on(table.entityId, table.type),
      index('idx_payment_reminders_email_template_id').on(table.emailTemplateId),
    ]
  },
)

export const paymentReminderDeliveries = pgTable(
  'payment_reminder_deliveries',
  {
    id,
    entityId: text('entity_id')
      .notNull()
      .references(() => entities.id),
    paymentReminderId: text('payment_reminder_id')
      .notNull()
      .references(() => paymentReminders.id),
    receivableId: text('receivable_id')
      .notNull()
      .references(() => receivables.id),
    emailId: text('email_id').notNull(),
    sentAt: createdAt,
  },
  (table) => {
    return [
      index('idx_payment_reminder_deliveries_entity_id').on(table.entityId),
      index('idx_payment_reminder_deliveries_reminder_id').on(table.paymentReminderId),
      index('idx_payment_reminder_deliveries_receivable_id').on(table.receivableId),
      uniqueIndex('uidx_payment_reminder_deliveries_unique_send').on(
        table.paymentReminderId,
        table.receivableId,
      ),
    ]
  },
)

export type ReminderEmailTemplateRecord = typeof reminderEmailTemplates.$inferSelect
export type ReminderEmailTemplateInsert = typeof reminderEmailTemplates.$inferInsert

export type PaymentReminderRecord = typeof paymentReminders.$inferSelect
export type PaymentReminderInsert = typeof paymentReminders.$inferInsert

export type PaymentReminderDeliveryRecord = typeof paymentReminderDeliveries.$inferSelect
export type PaymentReminderDeliveryInsert = typeof paymentReminderDeliveries.$inferInsert

const reminderEmailTemplateInsertSchema = createInsertSchema(reminderEmailTemplates)
export const reminderEmailTemplateUserInputSchema = reminderEmailTemplateInsertSchema.omit({
  entityId: true,
  createdAt: true,
  updatedAt: true,
})

const paymentReminderInsertSchema = createInsertSchema(paymentReminders)
export const paymentReminderUserInputSchema = paymentReminderInsertSchema.omit({
  entityId: true,
  createdAt: true,
  updatedAt: true,
})

export const reminderEmailTemplatesRelations = relations(reminderEmailTemplates, ({ one, many }) => ({
  entity: one(entities, {
    fields: [reminderEmailTemplates.entityId],
    references: [entities.id],
  }),
  paymentReminders: many(paymentReminders),
}))

export const paymentRemindersRelations = relations(paymentReminders, ({ one, many }) => ({
  entity: one(entities, {
    fields: [paymentReminders.entityId],
    references: [entities.id],
  }),
  emailTemplate: one(reminderEmailTemplates, {
    fields: [paymentReminders.emailTemplateId],
    references: [reminderEmailTemplates.id],
  }),
  deliveries: many(paymentReminderDeliveries),
}))

export const paymentReminderDeliveriesRelations = relations(paymentReminderDeliveries, ({ one }) => ({
  entity: one(entities, {
    fields: [paymentReminderDeliveries.entityId],
    references: [entities.id],
  }),
  paymentReminder: one(paymentReminders, {
    fields: [paymentReminderDeliveries.paymentReminderId],
    references: [paymentReminders.id],
  }),
  receivable: one(receivables, {
    fields: [paymentReminderDeliveries.receivableId],
    references: [receivables.id],
  }),
}))
